import * as React from "react";
import * as ReactQuery from "react-query";
import axios from "axios";

import {
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  useReactQueryClient,
} from "../../../Common";

import { DataSource_MyBag_QueryKey } from "../data-source--mybag-provider";

import { DataMutationMyBag_GiftcardApply_QueryKey } from "./data-mutation--mybag-giftcard-apply-query-key-factory";

export interface DataMutationMyBagGiftcardApplyConsumerInterface {
  data: DataMutationMyBagGiftcardApply_Result["data"];
  request: DataMutationMyBagGiftcardApply_Result["variables"];
  status: DataMutationMyBagGiftcardApply_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationMyBagGiftcardApplyConsumerInterface;
const DataMutationMyBagGiftcardApplyContext = React.createContext(defaultValue);

export interface DataMutationMyBagGiftcardApply_ControllerInterface {
  mutate: DataMutationMyBagGiftcardApply_Result["mutate"];
  mutateAsync: DataMutationMyBagGiftcardApply_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationMyBagGiftcardApply_ControllerInterface;
const DataMutationMyBagGiftcardApply_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationMyBagGiftcardApply_Request {
  quote_id: string;
  giftcard_number: string;
  giftcard_pin: string;
  country_code: string;
  is_guest: boolean;
}
export type DataMutationMyBagGiftcardApply_Result =
  ReactQuery.UseMutationResult<
    DataMutationMyBagGiftcardApplyResponse,
    unknown,
    DataMutationMyBagGiftcardApply_Request,
    unknown
  >;

export interface DataMutationMyBagGiftcardApplyResponse {
  success?: boolean;
  error?: DataMutationMyBagGiftcardApplyResponse_GenericProxyError;
}
export interface DataMutationMyBagGiftcardApplyResponse_GenericProxyError {
  type?: string;
  title?: string;
}
export type GraphqlResponse_MyBagGiftcardApply = GraphqlWrappedData<
  "bbntApplyGiftcardToCart",
  DataMutationMyBagGiftcardApplyResponse
>;

export interface DataMutationMyBagGiftcardApplyProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationMyBagGiftcardApplyProvider: React.FunctionComponent<
  DataMutationMyBagGiftcardApplyProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const queryClient = useReactQueryClient();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationMyBag_GiftcardApply_QueryKey.mybagGiftcardApply();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    DataMutationMyBagGiftcardApplyResponse,
    DataMutationMyBagGiftcardApply_Request
  > = React.useCallback(
    async (request) => {
      const {
        quote_id,
        giftcard_number,
        giftcard_pin,
        country_code,
        is_guest,
      } = request;

      const gqlQuery = `
        mutation {
          bbntApplyGiftcardToCart(
            quote_id: "${quote_id}"
            giftcard_number: "${giftcard_number}"
            giftcard_pin: "${giftcard_pin}"
            country_code: "${country_code}"
            is_guest: ${!!is_guest}
          ) {
            success
            error {
              title
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_MyBagGiftcardApply>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      let errorMessage: string | undefined;
      if (data) {
        if (data.errors) {
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            const firstError = data.errors[0];
            if (firstError) {
              // M2-2098: a present error must always surface something, even
              // when its message is empty/undefined.
              errorMessage = firstError.message || "An Unknown Error has occured";
            }
          }
        } else if (data.data) {
          if (data.data.bbntApplyGiftcardToCart) {
            const bbntApplyGiftcardToCart = data.data.bbntApplyGiftcardToCart;
            if (bbntApplyGiftcardToCart.error) {
              // M2-2098: throw on the presence of an error object, not just a
              // truthy title — a null/empty title used to fall through silently.
              errorMessage =
                bbntApplyGiftcardToCart.error.title ||
                "An Unknown Error has occured";
            } else if (!bbntApplyGiftcardToCart.success) {
              errorMessage = "An Unknown Error has occured";
            }
          }
        }
      }
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      const { data: gqlResponseData } = data || {};
      const { bbntApplyGiftcardToCart } = gqlResponseData || {};

      (window as any).require(
        ["Magento_Checkout/js/model/step-navigator"],
        (stepNavigator: any) => {
          const currentStepIsStale = true;
          stepNavigator.handleHash(currentStepIsStale);
        }
      );

      return bbntApplyGiftcardToCart;
    },
    [initialState]
  );

  const onSuccess = React.useCallback(() => {
    queryClient.invalidateQueries(DataSource_MyBag_QueryKey.mybagBaseQuery);
  }, []);

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess,
  });

  const value: DataMutationMyBagGiftcardApplyConsumerInterface = React.useMemo(
    () => ({
      data: mutation.data,
      request: mutation.variables,
      status: mutation.status,
      error: mutation.error,
    }),
    [mutation.data, mutation.variables, mutation.status, mutation.error]
  );
  const controllerValue: DataMutationMyBagGiftcardApply_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationMyBagGiftcardApplyContext.Provider value={value}>
      <DataMutationMyBagGiftcardApply_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationMyBagGiftcardApply_ControllerContext.Provider>
    </DataMutationMyBagGiftcardApplyContext.Provider>
  );
};

export const useDataMutationMyBagGiftcardApply = () => {
  const context = React.useContext(DataMutationMyBagGiftcardApplyContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationMyBagGiftcardApply must be used within a DataMutationMyBagGiftcardApplyProvider"
    );
  }
  return context;
};

export const useDataMutationMyBagGiftcardApply_asController = () => {
  const context = React.useContext(
    DataMutationMyBagGiftcardApply_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationMyBagGiftcardApply_asController must be used within a DataMutationMyBagGiftcardApplyProvider"
    );
  }
  return context;
};
